import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, User, Activity, CheckCircle2, Clock } from "lucide-react";

function useCoaches() {
  return useQuery({
    queryKey: ["coaches-all"],
    queryFn: async () => (await apiClient.get("/coaches")).data,
  });
}

function useMembers() {
  return useQuery({
    queryKey: ["members-all"],
    queryFn: async () => (await apiClient.get("/members")).data,
  });
}

function AssignDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data: coaches = [] } = useCoaches();
  const { data: members = [] } = useMembers();
  const [memberId, setMemberId] = useState("");
  const [coachId, setCoachId] = useState("");
  const [due, setDue] = useState("");
  const [kind, setKind] = useState("FIRST");
  const create = useMutation({
    mutationFn: async () => (await apiClient.post("/assessment-assignments", {
      member_id: memberId, coach_id: coachId, due_date: due || null, kind,
    })).data,
    onSuccess: () => {
      toast.success("Assessment assigned");
      qc.invalidateQueries({ queryKey: ["assessment-assignments"] });
      onOpenChange(false); setMemberId(""); setCoachId(""); setDue(""); setKind("FIRST");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Assign Assessment</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Branch Admin assigns a coach to conduct a member assessment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Member</Label>
            <Select value={memberId} onValueChange={setMemberId}>
              <SelectTrigger data-testid="member-select" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue placeholder="Pick a member" /></SelectTrigger>
              <SelectContent>{members.map((m) => (<SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Coach</Label>
            <Select value={coachId} onValueChange={setCoachId}>
              <SelectTrigger data-testid="coach-select" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue placeholder="Pick a coach" /></SelectTrigger>
              <SelectContent>{coaches.map((c) => (<SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Due Date</Label>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="due-date-input" className="bg-black border-zinc-800 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="kind-select" className="bg-black border-zinc-800 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="FIRST">First (new member)</SelectItem>
                  <SelectItem value="QUARTERLY">Quarterly reassessment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!memberId || !coachId || create.isPending}
            data-testid="confirm-assign-btn" className="bg-volt hover:bg-volt-hover text-black rounded-sm">
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssessmentRow({ label, k, unit, form, upd }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}{unit ? ` (${unit})` : ""}</Label>
      <Input type="number" step="0.01" value={form[k]} onChange={upd(k)} data-testid={`inp-${k}`} className="bg-black border-zinc-800 rounded-sm mt-1" />
    </div>
  );
}

function CompleteDialog({ assignment, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    weight_kg: "", bf_pct: "", muscle_mass_kg: "", bmi: "",
    pushup_max: "", pullup_max: "", plank_sec: "",
    row_2k_sec: "", sprint_500m_sec: "", vo2_max_est: "",
    bench_kg: "", squat_kg: "", deadlift_kg: "", oh_press_kg: "", clean_kg: "",
    mobility_score: "", posture_notes: "",
    notes: "",
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const num = (v) => v === "" ? null : Number(v);
  const submit = useMutation({
    mutationFn: async () => (await apiClient.post(`/assessment-assignments/${assignment.id}/complete`, {
      user_id: assignment.member_id,
      body_composition: {
        weight_kg: num(form.weight_kg), bf_pct: num(form.bf_pct),
        muscle_mass_kg: num(form.muscle_mass_kg), bmi: num(form.bmi),
      },
      musculoskeletal: {
        mobility_score: num(form.mobility_score), posture_notes: form.posture_notes || null,
      },
      strength: {
        pushup_max: num(form.pushup_max), pullup_max: num(form.pullup_max), plank_sec: num(form.plank_sec),
      },
      endurance: {
        row_2k_sec: num(form.row_2k_sec), sprint_500m_sec: num(form.sprint_500m_sec), vo2_max_est: num(form.vo2_max_est),
      },
      one_rm: {
        bench_kg: num(form.bench_kg), squat_kg: num(form.squat_kg), deadlift_kg: num(form.deadlift_kg),
        oh_press_kg: num(form.oh_press_kg), clean_kg: num(form.clean_kg),
      },
      notes: form.notes || null,
    })).data,
    onSuccess: () => {
      toast.success("Assessment logged. Next reassessment auto-scheduled in 3 months.");
      qc.invalidateQueries({ queryKey: ["assessment-assignments"] });
      onOpenChange(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (!assignment) return null;
  return (
    <Dialog open={!!assignment} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">Log Assessment · {assignment.member_name}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">Kind: {assignment.kind} · Due: {assignment.due_date}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Section title="Body Composition">
            <AssessmentRow form={form} upd={upd} label="Weight" k="weight_kg" unit="kg" />
            <AssessmentRow form={form} upd={upd} label="Body Fat" k="bf_pct" unit="%" />
            <AssessmentRow form={form} upd={upd} label="Muscle Mass" k="muscle_mass_kg" unit="kg" />
            <AssessmentRow form={form} upd={upd} label="BMI" k="bmi" />
          </Section>
          <Section title="Musculoskeletal">
            <AssessmentRow form={form} upd={upd} label="Mobility (1-5)" k="mobility_score" />
            <div className="col-span-3">
              <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Posture Notes</Label>
              <Textarea value={form.posture_notes} onChange={upd("posture_notes")} rows={2} data-testid="inp-posture_notes" className="bg-black border-zinc-800 rounded-sm mt-1" />
            </div>
          </Section>
          <Section title="Strength (reps)">
            <AssessmentRow form={form} upd={upd} label="Pushups" k="pushup_max" />
            <AssessmentRow form={form} upd={upd} label="Pullups" k="pullup_max" />
            <AssessmentRow form={form} upd={upd} label="Plank" k="plank_sec" unit="s" />
          </Section>
          <Section title="Endurance">
            <AssessmentRow form={form} upd={upd} label="2km Row" k="row_2k_sec" unit="s" />
            <AssessmentRow form={form} upd={upd} label="500m Sprint" k="sprint_500m_sec" unit="s" />
            <AssessmentRow form={form} upd={upd} label="VO2 Max est" k="vo2_max_est" />
          </Section>
          <Section title="1 Rep Max (kg)">
            <AssessmentRow form={form} upd={upd} label="Bench" k="bench_kg" />
            <AssessmentRow form={form} upd={upd} label="Squat" k="squat_kg" />
            <AssessmentRow form={form} upd={upd} label="Deadlift" k="deadlift_kg" />
            <AssessmentRow form={form} upd={upd} label="OH Press" k="oh_press_kg" />
            <AssessmentRow form={form} upd={upd} label="Clean" k="clean_kg" />
          </Section>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Coach Notes</Label>
            <Textarea value={form.notes} onChange={upd("notes")} rows={3} data-testid="inp-notes" className="bg-black border-zinc-800 rounded-sm mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(null)} className="rounded-sm">Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending} data-testid="submit-assessment"
            className="bg-volt hover:bg-volt-hover text-black rounded-sm">
            <CheckCircle2 size={14} className="mr-1" /> Save Assessment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-bold mb-2">{title}</div>
      <div className="grid grid-cols-3 gap-2">{children}</div>
    </div>
  );
}

export default function AssessmentsPage() {
  const { user } = useAuth();
  const [assignOpen, setAssignOpen] = useState(false);
  const [completeFor, setCompleteFor] = useState(null);
  const { data: rows = [] } = useQuery({
    queryKey: ["assessment-assignments"],
    queryFn: async () => (await apiClient.get("/assessment-assignments")).data,
  });
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  const isCoach = user?.role === "COACH";
  const badge = (s) => ({ PENDING: "bg-volt/20 text-volt", COMPLETED: "bg-green-500/20 text-green-400", OVERDUE: "bg-red-500/20 text-red-400" }[s] || "bg-zinc-700 text-zinc-300");
  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="assessments-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Member Wellness</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Assessments</h1>
          <div className="text-zinc-500 text-sm mt-1">First on join · Quarterly reassessments · Auto-scheduled.</div>
        </div>
        {isAdmin && (
          <Button onClick={() => setAssignOpen(true)} data-testid="assign-btn"
            className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm">
            + Assign Assessment
          </Button>
        )}
      </div>

      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} />
      <CompleteDialog assignment={completeFor} onOpenChange={setCompleteFor} />

      <Panel>
        <PanelHeader title={isCoach ? "My Assignments" : "All Assignments"} subtitle={`${rows.length} total`} />
        {rows.length === 0 ? (
          <div className="text-zinc-500 text-sm py-8 text-center">Nothing here yet.</div>
        ) : (
          <div className="divide-y divide-zinc-900">
            {rows.map((r) => (
              <div key={r.id} className="py-3 flex items-center gap-3" data-testid={`assignment-${r.id}`}>
                <div className={`text-[9px] uppercase tracking-[0.16em] font-bold px-2 py-0.5 rounded-sm ${badge(r.status)}`}>{r.status}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate flex items-center gap-2">
                    <User size={12} className="text-zinc-500" />{r.member_name}
                    <span className="text-[10px] text-zinc-500 uppercase tracking-[0.14em]">{r.kind}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 flex items-center gap-3 mt-0.5">
                    <span><Activity size={10} className="inline mr-1" />{r.coach_name}</span>
                    <span><Calendar size={10} className="inline mr-1" />Due {r.due_date}</span>
                    {r.completed_at && <span><Clock size={10} className="inline mr-1" />Done {r.completed_at.slice(0, 10)}</span>}
                  </div>
                </div>
                {r.status !== "COMPLETED" && (isCoach || isAdmin) && (
                  <Button size="sm" onClick={() => setCompleteFor(r)} data-testid={`complete-${r.id}`}
                    className="bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.16em]">
                    Log Assessment
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
